// Data-driven block library for SuggestionCard.
//
// Each entry in BLOCKS is a self-guarding render fn `(ctx) => ReactNode | null`
// that returns null when its evidence is absent — so a card shows only the
// blocks the backend actually populated. ARCHETYPE_LAYOUT maps an archetype to
// an ordered list of block keys (generalized from the modal's composition; no
// per-rule branching). Adding/reordering a card = edit the map below.
import React from 'react';

import { toNum } from 'utils/suggestionEvidence';
import { buildEntityDeepLink } from 'utils/suggestionActions';
import CriteriaWithBars from './subcomponents/CriteriaWithBars';
import ContextMetricsBar from './subcomponents/ContextMetricsBar';
import WindowComparisonTable from './subcomponents/WindowComparisonTable';
import DiagnosticScoreCard from './subcomponents/DiagnosticScoreCard';
import SubItemsTable from './subcomponents/SubItemsTable';
import PlacementsSubItemsTable, { isPlacementsSubItems } from './subcomponents/PlacementsSubItemsTable';
import CreativePreview from './subcomponents/CreativePreview';
import EntityChangeHeader from './subcomponents/EntityChangeHeader';
import ValueChangeBlock from './subcomponents/ValueChangeBlock';
import EntityTable from './subcomponents/EntityTable';
import MetricSingle from './subcomponents/MetricSingle';
import StatusPill from './subcomponents/StatusPill';
import ItemCard from './subcomponents/ItemCard';
import CpaStreakChart from './subcomponents/CpaStreakChart';
import RoasStreakChart from './subcomponents/RoasStreakChart';
// Note: the card description now renders in the header (SuggestionCard), not as a
// bottom "Ver descripción completa" accordion — so UserTextAccordion is not used here.

const ALERT_SEVERITY = {
  high: { tone: 'danger', label: 'Alerta alta' },
  medium: { tone: 'warning', label: 'Alerta media' },
  low: { tone: 'info', label: 'Alerta baja' },
};

const roasText = (v) => (v != null ? `${Number(v).toFixed(1)}x` : '—');

// ── blocks ───────────────────────────────────────────────────────────────────

function entityHeader(ctx) {
  // When the picker lists every ad (incl. the primary), the standalone single-entity
  // header would just duplicate the first row, so hide it. (Value-led layouts like D
  // keep their header and the picker lists only the OTHER items — hideHeader is false.)
  if (ctx.hideHeader) return null;
  const { suggestion: s, resolved } = ctx;
  const entity = s?.primary_entity || resolved.visibleItems?.[0]?.target_entity || null;
  const breadcrumb = Array.isArray(s?.primary_entity_breadcrumb) ? s.primary_entity_breadcrumb : [];
  const severity = ALERT_SEVERITY[String(resolved.windowComparison?.alert_severity || '').toLowerCase()];
  // The action_label pill (e.g. "Pausar anuncio") now lives in the card header,
  // next to the status pill, as an outlined chip — see SuggestionCard. It is no
  // longer rendered here.
  if (!entity && !severity) return null;
  return (
    <div className="rounded-2xl overflow-hidden border border-white/10 bg-white/5">
      <div className="px-4 py-3 flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <CreativePreview entity={entity} subtitle={breadcrumb} size="lg" />
        </div>
        {severity ? <StatusPill label={severity.label} tone={severity.tone} /> : null}
      </div>
    </div>
  );
}

function criteriaBars(ctx) {
  const { resolved, intent, currency, criteriaTitle } = ctx;
  if (!Array.isArray(resolved.criteria) || resolved.criteria.length === 0) return null;
  // When the card lists ≥2 ad rows above this block (multiItems precedes criteriaBars),
  // each row already shows the per-item criteria chips — so these suggestion-level tiles
  // would just restate the primary/first ad. Skip them to avoid the duplication.
  if (ctx.suppressRedundantCriteria) return null;
  return (
    <CriteriaWithBars
      title={criteriaTitle}
      criteria={resolved.criteria}
      metrics={resolved.metricsExtended}
      currency={currency}
      intentTone={intent}
    />
  );
}

function contextMetrics(ctx) {
  const { resolved } = ctx;
  const aw = resolved.analysisWindow;
  const hasPeriod = Boolean(aw && (aw.label || (aw.start && aw.end)));
  // Admin cards show ONLY the "Período: …" chip here — no metric tiles. The
  // per-criterion values already render in the criteria block above, so the
  // context bar is intentionally period-only. Passing metricKeysToShow={[]}
  // makes ContextMetricsBar emit just the period prefix; the shared component
  // is left untouched so the non-admin V2 modal keeps its full metric bar.
  if (!hasPeriod) return null;
  return <ContextMetricsBar analysisWindow={aw} metricKeysToShow={[]} />;
}

function windowComparison(ctx) {
  const { resolved, currency } = ctx;
  const wc = resolved.windowComparison;
  if (!wc || !Array.isArray(wc.windows) || wc.windows.length < 2) return null;
  const key = String(wc.metric_key || '').toLowerCase();
  // The per-window target was the chart's dashed line; carry it into the table as
  // the "vs objetivo" column. CTR has no target (the table shows a 7v7 delta instead).
  const target = key.includes('cpa')
    ? toNum(resolved.metricsExtended?.target_cpa)
    : key.includes('roas')
      ? toNum(resolved.metricsExtended?.target_roas)
      : null;
  return <WindowComparisonTable windowComparison={wc} target={target} currency={currency} />;
}

function valueChange(ctx) {
  const { suggestion: s, resolved, intent } = ctx;
  const vcd = resolved.valueChangeDisplay; // value_change_display (D) or synthesized from value_before/after (B/C)
  const items = resolved.visibleItems || [];
  const isMulti = items.length > 1 && items.some((it) => it?.value_change != null);
  if (!vcd && !isMulti) return null;
  // The proposed "after" value is editable in single-value mode when the host
  // provides the controlled state (SuggestionCard does).
  const afterEditable = !isMulti && Boolean(vcd) && typeof ctx.setValueAfterRaw === 'function';

  // Archetype D leads with the combined entity + before/after header (its layout has
  // no entityHeader block). Other archetypes (B/C) already render entityHeader above,
  // so here we show only the standalone before→after value block.
  if (String(s?.archetype || '').toUpperCase() === 'D') {
    const rawEntity = s?.primary_entity || items[0]?.target_entity || null;
    // EntityIdentityRow (inside EntityChangeHeader) reads entity.url; map permalink_url.
    const entity = rawEntity
      ? { ...rawEntity, url: rawEntity.permalink_url ?? rawEntity.url ?? null }
      : null;
    return (
      <EntityChangeHeader
        primaryEntity={entity}
        breadcrumb={Array.isArray(s?.primary_entity_breadcrumb) ? s.primary_entity_breadcrumb : []}
        actionLabel={s?.action_label}
        intent={intent}
        valueChangeDisplay={vcd}
        itemsCount={isMulti ? items.length : 1}
        entityTypePlural={s?.primary_entity?.type || 'items'}
        afterEditable={afterEditable}
        valueAfterRaw={ctx.valueAfterRaw}
        onChangeValueAfterRaw={ctx.setValueAfterRaw}
      />
    );
  }

  if (!vcd) return null;
  return (
    <ValueChangeBlock
      valueChangeDisplay={vcd}
      intent={intent}
      afterEditable={afterEditable}
      afterRawValue={ctx.valueAfterRaw}
      onAfterRawChange={ctx.setValueAfterRaw}
    />
  );
}

function diagnosticScore(ctx) {
  if (!ctx.resolved.diagnosticScore) return null;
  return <DiagnosticScoreCard diagnosticScore={ctx.resolved.diagnosticScore} intent={ctx.intent} />;
}

function subItems(ctx) {
  const { resolved, currency, suggestion: s, selectedIds, toggleSelected } = ctx;
  const items = Array.isArray(resolved.subItems) ? resolved.subItems : [];
  if (items.length === 0) return null;
  const selectable = Array.isArray(selectedIds) && typeof toggleSelected === 'function';
  const sel = selectable ? selectedIds : null;
  const onToggle = selectable ? toggleSelected : null;
  if (isPlacementsSubItems(items)) {
    return (
      <PlacementsSubItemsTable
        subItems={items}
        subItemsSummary={resolved.subItemsSummary}
        currency={currency}
        selectedIds={sel}
        onToggle={onToggle}
      />
    );
  }
  return (
    <SubItemsTable
      subItems={items}
      subItemsSummary={resolved.subItemsSummary}
      currency={currency}
      primaryKpi={s?.primary_kpi}
      selectedIds={sel}
      onToggle={onToggle}
    />
  );
}

function dailyStreak(ctx) {
  const ds = ctx.resolved.dailyStreak;
  if (!ds) return null;
  if (ds.type === 'cpa') return <CpaStreakChart streak={ds.streak} currency={ctx.currency} />;
  if (ds.type === 'roas') return <RoasStreakChart streak={ds.streak} />;
  return null;
}

function peerComparison(ctx) {
  const rows = ctx.resolved.peerComparison;
  if (!rows || rows.length === 0) return null;
  const { currency } = ctx;
  // Color ROAS by how each row stands against the rule trigger: the flagged ad
  // ("Este") underperforms the winners (red); winners / rows above it are good (green).
  const currentRoas = rows.find((r) => r.isCurrent)?.roas ?? null;
  const roasTone = (r) => {
    if (r.isCurrent) return 'text-rose-300';
    if (r.isWinner) return 'text-emerald-300';
    if (currentRoas != null && r.roas != null) return r.roas >= currentRoas ? 'text-emerald-300' : 'text-rose-300';
    return 'text-white';
  };
  return (
    <EntityTable
      title="Comparativa en el mismo conjunto"
      description="Compará contra anuncios que están ganando subastas y volumen."
      rows={rows.slice(0, 4)}
      getRowKey={(r, i) => `${r?.adName ?? 'ad'}-${i}`}
      getRowVariant={(r) => (r.isCurrent ? 'highlight' : 'successSubtle')}
      columns={[
        {
          key: 'ad',
          label: 'Anuncio',
          colSpan: 2,
          render: (r) => (
            <div className="flex items-center gap-2 min-w-0">
              <span className="truncate text-white font-semibold" title={r.adName || ''}>{r.adName || '—'}</span>
              {r.isWinner ? <StatusPill label="Ganador" tone="success" /> : (r.isCurrent ? <StatusPill label="Este" tone="info" /> : null)}
            </div>
          ),
        },
        { key: 'spend', label: 'Gasto', align: 'right', render: (r) => <MetricSingle value={r.spend} hint="currency" currency={currency} /> },
        { key: 'roas', label: 'ROAS', align: 'right', render: (r) => <span className={`font-semibold tabular-nums ${roasTone(r)}`}>{roasText(r.roas)}</span> },
        { key: 'conv', label: 'Conv.', align: 'right', render: (r) => <MetricSingle value={r.conversions} hint="integer" /> },
      ]}
    />
  );
}

function adsetOverlap(ctx) {
  const rows = ctx.resolved.adsetOverlap;
  if (!rows || rows.length === 0) return null;
  const { currency } = ctx;
  const hasSpend = rows.some((r) => r.spendA != null || r.spendB != null);
  const hasRoas = rows.some((r) => r.roasA != null || r.roasB != null);
  const columns = [
    { key: 'a', label: 'AdSet A', colSpan: 2, render: (r) => <span className="truncate text-white" title={r.adsetAName || ''}>{r.adsetAName || '—'}</span> },
    { key: 'b', label: 'AdSet B', colSpan: 2, render: (r) => <span className="truncate text-white" title={r.adsetBName || ''}>{r.adsetBName || '—'}</span> },
    {
      key: 'overlap',
      label: 'Overlap',
      align: 'right',
      render: (r) => (r.overlapPct != null
        ? <StatusPill label={`${Math.round(r.overlapPct * 100)}%`} tone={r.overlapPct >= 0.4 ? 'danger' : 'neutral'} tabularNums />
        : '—'),
    },
  ];
  if (hasSpend) {
    columns.push({ key: 'spend', label: 'Gasto', align: 'right', render: (r) => <MetricSingle value={(r.spendA ?? 0) + (r.spendB ?? 0)} hint="currency" currency={currency} /> });
  }
  if (hasRoas) {
    columns.push({
      key: 'roas',
      label: 'ROAS',
      align: 'right',
      render: (r) => {
        const vals = [r.roasA, r.roasB].filter((x) => x != null);
        return vals.length ? roasText(vals.reduce((a, b) => a + b, 0) / vals.length) : '—';
      },
    });
  }
  return (
    <EntityTable
      title="Overlaps entre AdSets"
      description="Cuando dos AdSets se superponen, compiten entre sí en la subasta."
      rows={rows}
      maxRows={6}
      getRowKey={(r, i) => `${r.adsetAName ?? 'a'}-${r.adsetBName ?? 'b'}-${i}`}
      columns={columns}
    />
  );
}

function audienceExpansion(ctx) {
  const rows = ctx.resolved.audienceExpansion;
  if (!rows || rows.length === 0) return null;
  const { currency } = ctx;
  return (
    <EntityTable
      title="Audiencia"
      description="Compará el tamaño de tu segmentación actual vs la opción Broad."
      rows={rows}
      maxRows={6}
      getRowKey={(r, i) => `${r.label ?? 'seg'}-${i}`}
      getRowVariant={(r) => (typeof r.label === 'string' && r.label.toLowerCase().includes('broad') ? 'success' : (r.isCurrent ? 'highlight' : 'default'))}
      columns={[
        {
          key: 'seg',
          label: 'Segmento',
          colSpan: 2,
          render: (r) => (
            <div className="min-w-0">
              <div className="truncate text-white font-semibold" title={r.label || ''}>{r.label || '—'}</div>
              {r.note ? <div className="text-[11px] text-white/55 truncate">{r.note}</div> : null}
            </div>
          ),
        },
        { key: 'size', label: 'Tamaño', align: 'right', render: (r) => <MetricSingle value={r.audienceSize} hint="integer" /> },
        { key: 'spend', label: 'Gasto', align: 'right', render: (r) => <MetricSingle value={r.spend} hint="currency" currency={currency} /> },
        { key: 'conv', label: 'Conv.', align: 'right', render: (r) => <MetricSingle value={r.conversions} hint="integer" /> },
        { key: 'cpa', label: 'CPA', align: 'right', render: (r) => <MetricSingle value={r.cpa} hint="currency" currency={currency} /> },
      ]}
    />
  );
}

function multiItems(ctx) {
  const { resolved, intent, currency, isItemsExpanded, setIsItemsExpanded, suggestion: s, selectedIds, toggleSelected, itemsPicker, itemsDeepLink, selectableItems } = ctx;
  // Picker (checkboxes) and deep-link lists both show the resolved set (incl. the primary,
  // in place of the header); otherwise it's an info-only list of affected items.
  const items = (itemsPicker || itemsDeepLink) ? (selectableItems || []) : (resolved.visibleItems || []);
  if (items.length <= 1) return null;
  const breadcrumb = Array.isArray(s?.primary_entity_breadcrumb) ? s.primary_entity_breadcrumb : [];
  const selectable = itemsPicker && Array.isArray(selectedIds) && typeof toggleSelected === 'function';
  // applicable:false cards can't be auto-applied — each row deep-links to its entity instead.
  const deepLinkMode = Boolean(itemsDeepLink);
  const deepLinkLabel = s?.cta_label || 'Ver';
  const idOf = (item) => (item?.id != null ? String(item.id) : null);
  const selectedCount = selectable
    ? items.filter((it) => { const id = idOf(it); return id == null || selectedIds.includes(id); }).length
    : items.length;
  return (
    <div>
      <button
        type="button"
        onClick={() => setIsItemsExpanded((v) => !v)}
        className="w-full flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-left hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
      >
        <div className="min-w-0">
          <div className="text-sm font-semibold text-white">{isItemsExpanded ? 'Ocultar' : 'Ver'} {items.length} items</div>
          <div className="text-[11px] text-white/55">
            {selectable ? `${selectedCount}/${items.length} seleccionados para aplicar` : 'Entidades afectadas por esta optimización'}
          </div>
        </div>
        <span className="text-white/60" aria-hidden>{isItemsExpanded ? '▴' : '▾'}</span>
      </button>
      {isItemsExpanded ? (
        <div className="mt-2 flex flex-col gap-2">
          {items.map((item, idx) => {
            const id = idOf(item);
            const rowSelectable = selectable && id != null;
            // Picker mode → checkbox; deep-link cards → per-row "Ver en {Platform}" link
            // (the entity's own permalink, else a built ads-manager URL); else read-only.
            const deepLinkUrl = deepLinkMode
              ? (item?.target_entity?.permalink_url
                || buildEntityDeepLink(item?.target_entity, s?.platform, s?.evidence?.account_id))
              : null;
            return (
              <ItemCard
                key={item?.id ?? idx}
                item={item}
                suggestionCriteria={resolved.criteria}
                intent={intent}
                currency={item?.value_change?.currency || currency}
                suggestionBreadcrumb={breadcrumb}
                suggestionMetricKeysToShow={resolved.metricKeysToShow}
                isApplied={Boolean(item?.applied_at)}
                selectable={rowSelectable}
                selected={rowSelectable ? selectedIds.includes(id) : false}
                onToggleSelect={rowSelectable ? () => toggleSelected(id) : null}
                deepLinkUrl={deepLinkUrl}
                deepLinkLabel={deepLinkLabel}
                onApply={null}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export const BLOCKS = {
  entityHeader,
  criteriaBars,
  contextMetrics,
  windowComparison,
  valueChange,
  diagnosticScore,
  subItems,
  dailyStreak,
  peerComparison,
  adsetOverlap,
  audienceExpansion,
  multiItems,
};

// Ordered block list per archetype. Each block self-guards on its evidence.
export const ARCHETYPE_LAYOUT = {
  A: ['entityHeader', 'multiItems', 'criteriaBars', 'contextMetrics', 'peerComparison'],
  B: ['entityHeader', 'multiItems', 'valueChange', 'criteriaBars', 'windowComparison', 'contextMetrics', 'dailyStreak'],
  C: ['entityHeader', 'valueChange', 'windowComparison', 'criteriaBars', 'contextMetrics'],
  D: ['valueChange', 'criteriaBars', 'windowComparison', 'contextMetrics', 'multiItems'],
  E: ['entityHeader', 'criteriaBars', 'contextMetrics', 'dailyStreak', 'peerComparison', 'multiItems'],
  F: ['entityHeader', 'criteriaBars', 'subItems', 'contextMetrics'],
  G: ['entityHeader', 'diagnosticScore', 'criteriaBars', 'contextMetrics'],
  H: ['entityHeader', 'multiItems', 'criteriaBars', 'contextMetrics', 'adsetOverlap', 'audienceExpansion'],
};

export const DEFAULT_LAYOUT = ['entityHeader', 'criteriaBars', 'contextMetrics', 'multiItems'];

// Criteria-section heading per archetype.
export const CRITERIA_TITLE = {
  D: 'Por qué califica',
  G: 'Señales detectadas',
};
export const DEFAULT_CRITERIA_TITLE = 'Por qué lo recomendamos';
